import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import type { Scope } from './ScopeTiles';
import { SectionHead } from './SectionHead';
import { SongRow } from './SongRow';
import type { Buckets } from '../derive-ui';
import type { PublishingTrack } from 'types/publishing';
import { ui } from '../ui';

interface Props {
  b: Buckets;
  legalName: string | null;
  scope: Scope | null;
  selected: string[];
  nSel: number;
  readOnly: boolean;
  onOpen: (t: PublishingTrack) => void;
  onCheck: (isrc: string, on: boolean) => void;
  onNotMine: (isrc: string) => void;
  onRestore: () => void;
  onHelp: () => void;
}

export function TrackLists({
  b,
  legalName,
  scope,
  selected,
  nSel,
  readOnly,
  onOpen,
  onCheck,
  onNotMine,
  onRestore,
  onHelp,
}: Props) {
  const { t } = useTranslation('publishing');
  const { missing, ready, blocked, done, hiddenCount } = b;
  const restoreBtn = (
    <button
      type="button"
      className={`${ui.link} disabled:cursor-not-allowed disabled:opacity-50`}
      disabled={readOnly}
      onClick={onRestore}
    >
      {t('restore', { count: hiddenCount })}
    </button>
  );

  return (
    <div>
      {!scope && !!blocked.length && (
        <div className={ui.card}>
          <SectionHead
            title={t('list_your_songs')}
            count={blocked.length}
            hint={t('list_your_songs_hint')}
          />
          <div>
            {blocked.map((x) => (
              <SongRow
                key={x.spotifyTrackId}
                track={x}
                legalName={legalName}
                readOnly={readOnly}
                onOpen={onOpen}
              />
            ))}
          </div>
        </div>
      )}

      {(!scope || scope === 'missing') && !!(missing.length || hiddenCount) && (
        <div className={ui.card}>
          <SectionHead
            title={t('list_not_listed')}
            count={missing.length}
            hint={t('list_not_listed_hint')}
          />
          {!!hiddenCount && <div className="mb-2">{restoreBtn}</div>}
          <div>
            {missing.map((x) => (
              <SongRow
                key={x.spotifyTrackId}
                track={x}
                legalName={legalName}
                check
                checked={!!x.isrc && selected.includes(x.isrc)}
                notMine
                pill="not_listed"
                readOnly={readOnly}
                onOpen={onOpen}
                onCheck={onCheck}
                onNotMine={onNotMine}
              />
            ))}
          </div>
          {!!missing.length && (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-4 border-t border-[#E9E9E9] pt-4">
              <span className={ui.muted}>
                {t('offer_cta_checked', { checked: t('checked_label', { count: nSel }) })}
              </span>
              <Button disabled={!nSel || readOnly} onClick={onHelp} className={ui.btnPrimary}>
                {t('register_label', { count: nSel })}
              </Button>
            </div>
          )}
        </div>
      )}

      {(!scope || scope === 'ready') && !!ready.length && (
        <div className={ui.card}>
          <SectionHead title={t('list_listed')} count={ready.length} hint={t('list_listed_hint')} />
          <div>
            {ready.map((x) => (
              <SongRow
                key={x.spotifyTrackId}
                track={x}
                legalName={legalName}
                pill="listed"
                readOnly={readOnly}
                onOpen={onOpen}
              />
            ))}
          </div>
        </div>
      )}

      {(!scope || scope === 'done') && !!done.length && (
        <div className={ui.card}>
          <SectionHead title={t('list_done')} count={done.length} hint={t('list_done_hint')} />
          {!!hiddenCount && scope === 'done' && <div className="mb-2">{restoreBtn}</div>}
          <div>
            {done.map((x) => (
              <SongRow
                key={x.spotifyTrackId}
                track={x}
                legalName={legalName}
                pill={x.notMine ? 'hidden' : 'sent'}
                readOnly={readOnly}
                onOpen={onOpen}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
